<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Gimnasio - Gestión de Rutinas y Coaches</span>
                    <v-spacer></v-spacer>
                    <v-btn color="secondary" text @click="goToCoachRoutines" class="mr-2">
                        <v-icon left>mdi-pencil</v-icon>
                        Crear Rutinas
                    </v-btn>
                    <v-btn color="primary" text @click="openAssignRoutineDialog" class="mr-2">
                        <v-icon left>mdi-plus</v-icon>
                        Asignar Rutina
                    </v-btn>
                </v-card-title>

                <!-- Tabs -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-dumbbell</v-icon>
                        Asignación de Rutinas
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-account-tie</v-icon>
                        Coaches de Gimnasio
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-history</v-icon>
                        Historial
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Asignación de Rutinas -->
                    <v-tab-item>
                        <!-- Estadísticas -->
                        <v-row class="mb-4">
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold primary--text">{{ totalAssignments }}</div>
                                    <div class="caption grey--text">Rutinas Asignadas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold green--text">{{ activeAssignments }}</div>
                                    <div class="caption grey--text">Activas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold orange--text">{{ completedAssignments }}</div>
                                    <div class="caption grey--text">Completadas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold info--text">{{ totalCoaches }}</div>
                                    <div class="caption grey--text">Coaches</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Tabla de Asignaciones -->
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-dumbbell</v-icon>
                                Rutinas Asignadas a Socios
                            </v-card-subtitle>
                            <v-data-table
                                :headers="assignmentHeaders"
                                :items="routineAssignments"
                                class="elevation-0"
                                :no-data-text="'Sin rutinas asignadas'">

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ getMemberName(item.member_id) }}</div>
                                    <div class="caption grey--text">Teléfono: {{ getMemberPhone(item.member_id) }}</div>
                                </template>

                                <template v-slot:item.routine="{ item }">
                                    <div class="font-weight-bold">{{ getRoutineName(item.routine_id) }}</div>
                                    <div class="caption grey--text">{{ getRoutineExerciseCount(item.routine_id) }} ejercicios</div>
                                </template>

                                <template v-slot:item.coach="{ item }">
                                    <div class="font-weight-bold">{{ getCoachName(item.coach_id) }}</div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-chip small :color="getStatusColor(item.status)" text-color="white">
                                        {{ getStatusLabel(item.status) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                small
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openEditDialog(item)">
                                                <v-icon small>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Editar</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewWorkoutPlan(item)">
                                                <v-icon small>mdi-file-document</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver plan de entrenamiento</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 2: Coaches de Gimnasio -->
                    <v-tab-item>
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-account-tie</v-icon>
                                Coaches y Calificaciones
                            </v-card-subtitle>
                            <v-data-table
                                :headers="coachHeaders"
                                :items="coaches"
                                class="elevation-0"
                                :no-data-text="'Sin coaches registrados'">

                                <template v-slot:item.name="{ item }">
                                    <div class="font-weight-bold">{{ item.name }}</div>
                                    <div class="caption grey--text">{{ item.specialty }}</div>
                                </template>

                                <template v-slot:item.rating="{ item }">
                                    <div class="d-flex align-center">
                                        <v-rating
                                            v-model="item.rating"
                                            :readonly="true"
                                            small
                                            color="amber"
                                            class="mr-2">
                                        </v-rating>
                                        <span class="caption">{{ item.rating.toFixed(1) }}</span>
                                    </div>
                                </template>

                                <template v-slot:item.reviews="{ item }">
                                    <v-chip small outlined>{{ item.reviews }} reseñas</v-chip>
                                </template>

                                <template v-slot:item.assigned_members="{ item }">
                                    <div class="font-weight-bold">{{ item.assigned_members }}</div>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                small
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openRatingDialog(item)">
                                                <v-icon small>mdi-star</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Calificar</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewCoachDetails(item)">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 3: Historial -->
                    <v-tab-item>
                        <v-card class="elevation-1">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-history</v-icon>
                                Historial de Asignaciones y Cambios
                            </v-card-subtitle>
                            <v-data-table
                                :headers="historyHeaders"
                                :items="assignmentHistory"
                                class="elevation-0"
                                :items-per-page="10"
                                :no-data-text="'Sin historial'"
                                sort-by="date"
                                :sort-desc="true">

                                <template v-slot:item.date="{ item }">
                                    <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                                    <div class="caption grey--text">{{ item.time }}</div>
                                </template>

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ item.member_name }}</div>
                                </template>

                                <template v-slot:item.action="{ item }">
                                    <v-chip small :color="getActionColor(item.action)" text-color="white">
                                        {{ getActionLabel(item.action) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.details="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon x-small v-bind="attrs" v-on="on" @click="viewHistory(item)">
                                                <v-icon x-small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Asignar Rutina -->
        <v-dialog v-model="assignRoutineDialog" max-width="700px" persistent>
            <v-card>
                <v-card-title>
                    <div>
                        <div class="headline">Asignar Rutina de Gimnasio</div>
                        <div class="caption grey--text">Seleccione un socio y una rutina</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="assignRoutineDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="assignForm" v-model="validAssign" lazy-validation>
                        <div class="subtitle-2 font-weight-bold mb-4">Información del Socio</div>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="newAssignment.member_id"
                                    :items="availableMembers"
                                    item-text="name"
                                    item-value="id"
                                    label="Socio"
                                    outlined
                                    dense
                                    :rules="[v => v || 'Se requiere seleccionar un socio']"
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">Seleccionar Rutina de Ejercicios</div>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="newAssignment.routine_id"
                                    :items="allRoutines"
                                    item-text="name"
                                    item-value="id"
                                    label="Rutina"
                                    outlined
                                    dense
                                    :rules="[v => v || 'Se requiere seleccionar una rutina']"
                                >
                                    <template v-slot:selection="{ item }">
                                        <div>
                                            <div class="body-2">{{ item.name }}</div>
                                            <div class="caption grey--text">{{ item.exercises.length }} ejercicios</div>
                                        </div>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <div>
                                            <div class="body-2">{{ item.name }}</div>
                                            <div class="caption grey--text">{{ item.exercises.length }} ejercicios • {{ getCoachName(item.coach_id) }}</div>
                                        </div>
                                    </template>
                                </v-select>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">Coach Responsable</div>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="newAssignment.coach_id"
                                    :items="gymService.coaches"
                                    item-text="name"
                                    item-value="id"
                                    label="Coach Asignado"
                                    outlined
                                    dense
                                    :rules="[v => v || 'Se requiere seleccionar un coach']"
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="assignRoutineDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveAssignment" :disabled="!validAssign">
                        <v-icon left>mdi-content-save</v-icon>
                        Asignar Rutina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Editar Asignación -->
        <v-dialog v-model="editDialog" max-width="700px" persistent>
            <v-card v-if="currentAssignment">
                <v-card-title>
                    <div>
                        <div class="headline">Editar Asignación</div>
                        <div class="caption grey--text">{{ getMemberName(currentAssignment.member_id) }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="editDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="editForm" v-model="validEdit" lazy-validation>
                        <div class="subtitle-2 font-weight-bold mb-4">Rutina Asignada</div>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="currentAssignment.routine_id"
                                    :items="allRoutines"
                                    item-text="name"
                                    item-value="id"
                                    label="Rutina"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="currentAssignment.coach_id"
                                    :items="gymService.coaches"
                                    item-text="name"
                                    item-value="id"
                                    label="Coach Responsable"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="currentAssignment.status"
                                    :items="statusOptions"
                                    label="Estado"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="editDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="updateAssignment" :disabled="!validEdit">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Detalles -->
        <v-dialog v-model="detailsDialog" max-width="700px">
            <v-card v-if="viewingAssignment">
                <v-card-title>
                    <div>
                        <div class="headline">Detalles de Asignación</div>
                        <div class="caption grey--text">{{ getMemberName(viewingAssignment.member_id) }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="detailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Socio</div>
                                <div class="body-2 font-weight-bold">{{ getMemberName(viewingAssignment.member_id) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Teléfono</div>
                                <div class="body-2">{{ getMemberPhone(viewingAssignment.member_id) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Rutina de Entrenamiento</div>
                                <div class="body-2 font-weight-bold">{{ getRoutineName(viewingAssignment.routine_id) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Ejercicios en la Rutina</div>
                                <div class="body-2">{{ getRoutineExerciseCount(viewingAssignment.routine_id) }} ejercicios</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Coach Responsable</div>
                                <div class="body-2 font-weight-bold">{{ getCoachName(viewingAssignment.coach_id) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Estado</div>
                                <v-chip small :color="getStatusColor(viewingAssignment.status)" text-color="white">
                                    {{ getStatusLabel(viewingAssignment.status) }}
                                </v-chip>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Fecha de Inicio</div>
                                <div class="body-2">{{ formatDate(viewingAssignment.start_date) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingAssignment.end_date">
                            <v-list-item-content>
                                <div class="caption grey--text">Fecha de Finalización</div>
                                <div class="body-2">{{ formatDate(viewingAssignment.end_date) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Calificar Coach -->
        <v-dialog v-model="ratingDialog" max-width="500px">
            <v-card v-if="ratingCoach">
                <v-card-title>
                    <div>
                        <div class="headline">Calificar Coach</div>
                        <div class="caption grey--text">{{ ratingCoach.name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="ratingDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="ratingForm" v-model="validRating" lazy-validation>
                        <div class="subtitle-2 font-weight-bold mb-4">Calificación</div>
                        <v-row class="justify-center mb-4">
                            <v-rating
                                v-model="newRating.stars"
                                :length="5"
                                size="48"
                                color="amber"
                                large
                                hover
                                :rules="[v => v >= 1 || 'Seleccione una calificación']"
                            ></v-rating>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="newRating.comment"
                                    label="Comentario (opcional)"
                                    outlined
                                    dense
                                    rows="3"
                                    placeholder="Comparta su experiencia con este coach"
                                    counter
                                    maxlength="300"
                                ></v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="ratingDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="submitRating" :disabled="!validRating">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Calificación
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Detalles del Coach -->
        <v-dialog v-model="coachDetailsDialog" max-width="600px">
            <v-card v-if="viewingCoach">
                <v-card-title>
                    <div>
                        <div class="headline">Detalles del Coach</div>
                        <div class="caption grey--text">{{ viewingCoach.name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="coachDetailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Nombre</div>
                                <div class="body-2 font-weight-bold">{{ viewingCoach.name }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Especialidad</div>
                                <div class="body-2">{{ viewingCoach.specialty }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Calificación</div>
                                <div class="d-flex align-center mt-2">
                                    <v-rating
                                        v-model="viewingCoach.rating"
                                        :readonly="true"
                                        small
                                        color="amber"
                                        class="mr-2">
                                    </v-rating>
                                    <span class="body-2 font-weight-bold">{{ viewingCoach.rating.toFixed(1) }}</span>
                                </div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Reseñas</div>
                                <div class="body-2">{{ viewingCoach.reviews }} calificaciones</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Socios Asignados</div>
                                <div class="body-2 font-weight-bold">{{ viewingCoach.assigned_members }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item v-if="viewingCoach.bio">
                            <v-list-item-content>
                                <div class="caption grey--text">Biografía</div>
                                <div class="body-2">{{ viewingCoach.bio }}</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import gymService from '@/services/gym.js'

export default {
  name: 'Gym',
  data: () => ({
    gymService,
    activeTab: 0,
    assignRoutineDialog: false,
    editDialog: false,
    detailsDialog: false,
    ratingDialog: false,
    coachDetailsDialog: false,
    validAssign: false,
    validEdit: false,
    validRating: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    currentAssignment: null,
    viewingAssignment: null,
    viewingCoach: null,
    ratingCoach: null,
    assignmentHeaders: [
      { text: 'Socio / Teléfono', value: 'member', width: '25%' },
      { text: 'Rutina / Ejercicios', value: 'routine', width: '20%' },
      { text: 'Coach', value: 'coach', width: '20%' },
      { text: 'Estado', value: 'status', width: '15%' },
      { text: 'Acciones', value: 'actions', width: '20%', sortable: false, align: 'center' }
    ],
    coachHeaders: [
      { text: 'Nombre / Especialidad', value: 'name', width: '25%' },
      { text: 'Calificación', value: 'rating', width: '20%' },
      { text: 'Reseñas', value: 'reviews', width: '15%', align: 'center' },
      { text: 'Socios Asignados', value: 'assigned_members', width: '15%', align: 'center' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    historyHeaders: [
      { text: 'Fecha / Hora', value: 'date', width: '20%' },
      { text: 'Socio', value: 'member', width: '25%' },
      { text: 'Acción', value: 'action', width: '30%' },
      { text: 'Detalles', value: 'details', width: '15%', sortable: false, align: 'center' }
    ],
    statusOptions: ['activa', 'pausada', 'completada', 'cancelada'],
    newAssignment: {
      member_id: null,
      routine_id: null,
      coach_id: null
    },
    newRating: {
      stars: 0,
      comment: ''
    }
  }),
  computed: {
    routineAssignments () {
      return gymService.routineAssignments
    },
    coaches () {
      return gymService.coaches
    },
    allRoutines () {
      return gymService.routines
    },
    availableMembers () {
      return gymService.members
    },
    assignmentHistory () {
      return gymService.history
    },
    totalAssignments () {
      return this.routineAssignments.length
    },
    activeAssignments () {
      return this.routineAssignments.filter(a => a.status === 'activa').length
    },
    completedAssignments () {
      return this.routineAssignments.filter(a => a.status === 'completada').length
    },
    totalCoaches () {
      return this.coaches.length
    }
  },
  methods: {
    getStatusColor (status) {
      const colors = {
        activa: 'green',
        pausada: 'orange',
        completada: 'blue',
        cancelada: 'red'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        activa: 'Activa',
        pausada: 'Pausada',
        completada: 'Completada',
        cancelada: 'Cancelada'
      }
      return labels[status] || 'Desconocido'
    },
    getActionColor (action) {
      const colors = {
        asignacion: 'primary',
        modificacion: 'warning',
        pausa: 'orange',
        completacion: 'success'
      }
      return colors[action] || 'grey'
    },
    getActionLabel (action) {
      const labels = {
        asignacion: 'Nueva Asignación',
        modificacion: 'Modificación',
        pausa: 'Pausada',
        completacion: 'Completada'
      }
      return labels[action] || 'Desconocido'
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getMemberName (memberId) {
      const member = gymService.getMemberById(memberId)
      return member ? member.name : 'Desconocido'
    },
    getMemberPhone (memberId) {
      const member = gymService.getMemberById(memberId)
      return member ? member.phone : 'N/A'
    },
    getCoachName (coachId) {
      const coach = gymService.getCoachById(coachId)
      return coach ? coach.name : 'Desconocido'
    },
    getRoutineName (routineId) {
      const routine = gymService.getRoutineById(routineId)
      return routine ? routine.name : 'Desconocida'
    },
    getRoutineExerciseCount (routineId) {
      const routine = gymService.getRoutineById(routineId)
      return routine ? routine.exercises.length : 0
    },
    goToCoachRoutines () {
      this.$router.push('/admin/coach-routines')
    },
    openAssignRoutineDialog () {
      this.newAssignment = {
        member_id: null,
        routine_id: null,
        coach_id: null
      }
      this.assignRoutineDialog = true
    },
    saveAssignment () {
      if (this.$refs.assignForm.validate()) {
        gymService.assignRoutineToMember(
          this.newAssignment.member_id,
          this.newAssignment.routine_id,
          this.newAssignment.coach_id
        )
        this.snackbarMessage = 'Rutina asignada correctamente'
        this.snackbarColor = 'success'
        this.snackbar = true
        this.assignRoutineDialog = false
      }
    },
    openEditDialog (assignment) {
      this.currentAssignment = JSON.parse(JSON.stringify(assignment))
      this.editDialog = true
    },
    updateAssignment () {
      if (this.$refs.editForm.validate()) {
        const index = gymService.routineAssignments.findIndex(a => a.id === this.currentAssignment.id)
        if (index > -1) {
          gymService.routineAssignments[index] = this.currentAssignment
        }
        this.snackbarMessage = 'Asignación actualizada correctamente'
        this.snackbarColor = 'success'
        this.snackbar = true
        this.editDialog = false
      }
    },
    viewDetails (assignment) {
      this.viewingAssignment = JSON.parse(JSON.stringify(assignment))
      this.detailsDialog = true
    },
    viewWorkoutPlan (assignment) {
      this.$router.push({
        path: '/admin/member-workout-plan',
        query: {
          member_id: assignment.member_id,
          assignment_id: assignment.id
        }
      })
    },
    viewHistory (record) {
      this.snackbarMessage = `${record.details}`
      this.snackbar = true
    },
    openRatingDialog (coach) {
      this.ratingCoach = JSON.parse(JSON.stringify(coach))
      this.newRating = {
        stars: 0,
        comment: ''
      }
      this.ratingDialog = true
    },
    submitRating () {
      if (this.$refs.ratingForm.validate()) {
        const coachIndex = gymService.coaches.findIndex(c => c.id === this.ratingCoach.id)
        if (coachIndex > -1) {
          const coach = gymService.coaches[coachIndex]
          const newRating = this.newRating.stars
          coach.rating = ((coach.rating * coach.reviews) + newRating) / (coach.reviews + 1)
          coach.reviews += 1
        }

        this.snackbarMessage = `Coach calificado con ${this.newRating.stars} estrellas`
        this.snackbarColor = 'success'
        this.snackbar = true
        this.ratingDialog = false
      }
    },
    viewCoachDetails (coach) {
      this.viewingCoach = JSON.parse(JSON.stringify(coach))
      this.coachDetailsDialog = true
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
